import { type DriveFile } from '@/shared/DriveTypes';
import { Modal } from '@mui/material';
import { bindDialog, type PopupState } from 'material-ui-popup-state/hooks';
import { darkTheme, lightTheme } from '~/theme.css';
import { LEGACY } from '~/utils/constants';
import { useMutateDriveFile } from '~/utils/hooks/hooks';
import { modalTypes, useStore, useTheme } from '~/utils/store';
import { type FileModalType } from '~/utils/types';
import { cx, focusInput, getId, isAFolder } from '~/utils/utils';
import { type AddNewLegacy } from '../AddMenu/AddNewContainer';
import * as styles from './AddModalLegacy.css';

export const AddModalLegacy = ({
  file,
  modalState,
  addNew,
}: {
  file: DriveFile;
  modalState: PopupState;
  addNew?: AddNewLegacy;
}) => {
  const modalType = useStore((state) => state.modalType);
  const theme = useTheme();
  const defaultValue = `Untitled ${modalType}`;
  const mutate = useMutateDriveFile();
  return (
    <Modal
      {...bindDialog(modalState)}
      className={cx({
        [darkTheme]: theme === 'dark',
        [lightTheme]: theme === 'light',
        [darkTheme]: theme === 'dark',
        [lightTheme]: theme === 'light',
      })}
      onClose={() => {
        modalState.close();
        queueMicrotask(() => {
          if (document.activeElement instanceof HTMLButtonElement) {
            document.activeElement.blur();
          }
        });
      }}
    >
      <form
        className={styles.modal}
        onSubmit={(e) => {
          e.preventDefault();
          const parents = isAFolder(file) ? ([getId(file)] satisfies [string]) : file.parents;
          if (!parents) throw new Error('file has no parents');
          const data = new FormData(e.currentTarget);
          const inputValue = data.get('input') as string;
          const linkValue = data.get('link') as string;
          const name =
            modalType === 'link' ? `[${inputValue || defaultValue}](${linkValue})` : inputValue || defaultValue;
          if (LEGACY) {
            addNew?.(
              name,
              parents,
              modalTypes[modalType as FileModalType].replace('application/vnd.google-apps.', '') as 'document'
            );
          } else {
            mutate('create', {
              name,
              mimeType: modalTypes[modalType as FileModalType],
              parents,
            });
          }

          modalState.close();
          queueMicrotask(() => {
            if (document.activeElement instanceof HTMLButtonElement) {
              document.activeElement.blur();
            }
          });
        }}
      >
        <h2 className={styles.modalTitle}>New {modalType}</h2>
        {modalType === 'link' && <div style={{ marginBottom: '.5rem' }}>Name (optional)</div>}
        <input ref={focusInput} defaultValue={`Untitled ${modalType}`} name="input" className={styles.modalInput} />
        {modalType === 'link' && (
          <>
            <div style={{ marginTop: '1.5rem', marginBottom: '.5rem' }}>Web address</div>
            <input
              name="link"
              pattern=".*(?:\.[a-z]{2}|:\d{2,}).*"
              required
              title="Enter a valid URL"
              placeholder="example.com"
              className={styles.modalInput}
            />
          </>
        )}
        <div className={styles.modalActions}>
          <button type="button" className={styles.cancel} onClick={() => modalState.close()}>
            Cancel
          </button>
          <button type="submit" className={styles.create}>
            Create
          </button>
        </div>
      </form>
    </Modal>
  );
};
